// src/services/personService.ts

import {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';
import {
  Person,
  CreatePersonInput,
  UpdatePersonInput,
  GetAllPeopleInput,
  SearchPeopleInput
} from '@/types/peopleTypes';

// Define types for Auth0 functions
type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const PEOPLE_ENDPOINT = `${API_BASE_URL}/people`;

/**
 * Helper function to acquire Auth0 access token.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn('Silent token acquisition failed for PersonService:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for PersonService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error('Failed to acquire access token interactively');
      }
      return token;
    }
    throw error;
  }
};

/**
 * Helper function to make authenticated API requests.
 */
const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Response> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `API request failed: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  return response;
};

/**
 * PersonService class for managing people.
 */
export class PersonService {
  constructor(
    private getAccessTokenSilently: GetAccessTokenSilentlyType,
    private getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ) {}

  /**
   * Create a new person.
   */
  async createPerson(input: CreatePersonInput): Promise<Person> {
    const response = await makeAuthenticatedRequest(
      PEOPLE_ENDPOINT,
      {
        method: 'POST',
        body: JSON.stringify(input)
      },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  /**
   * Get a person by ID.
   */
  async getPersonById(id: string, companyId: string): Promise<Person | null> {
    try {
      const response = await makeAuthenticatedRequest(
        `${PEOPLE_ENDPOINT}/${id}?companyId=${encodeURIComponent(companyId)}`,
        { method: 'GET' },
        this.getAccessTokenSilently,
        this.getAccessTokenWithPopup
      );

      return await response.json();
    } catch (error: any) {
      if (error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all people with pagination and search.
   */
  async getAllPeople(input: GetAllPeopleInput): Promise<Person[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('companyId', input.companyId);

    if (input.pageNumber !== undefined) {
      queryParams.append('pageNumber', input.pageNumber.toString());
    }
    if (input.pageSize !== undefined) {
      queryParams.append('pageSize', input.pageSize.toString());
    }
    if (input.searchQuery) {
      queryParams.append('searchQuery', input.searchQuery);
    }
    if (input.sortBy) {
      queryParams.append('sortBy', input.sortBy);
    }
    if (input.sortAscending !== undefined) {
      queryParams.append('sortAscending', input.sortAscending.toString());
    }

    const response = await makeAuthenticatedRequest(
      `${PEOPLE_ENDPOINT}?${queryParams.toString()}`,
      { method: 'GET' },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  /**
   * Update an existing person.
   */
  async updatePerson(input: UpdatePersonInput): Promise<Person> {
    const response = await makeAuthenticatedRequest(
      `${PEOPLE_ENDPOINT}/${input.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(input)
      },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  /**
   * Delete a person.
   */
  async deletePerson(id: string, companyId: string): Promise<boolean> {
    try {
      await makeAuthenticatedRequest(
        `${PEOPLE_ENDPOINT}/${id}?companyId=${encodeURIComponent(companyId)}`,
        { method: 'DELETE' },
        this.getAccessTokenSilently,
        this.getAccessTokenWithPopup
      );
      return true;
    } catch (error: any) {
      if (error.message.includes('404')) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Search people by term.
   */
  async searchPeople(input: SearchPeopleInput): Promise<Person[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('companyId', input.companyId);
    queryParams.append('searchTerm', input.searchTerm);

    if (input.maxResults !== undefined) {
      queryParams.append('maxResults', input.maxResults.toString());
    }

    const response = await makeAuthenticatedRequest(
      `${PEOPLE_ENDPOINT}/search?${queryParams.toString()}`,
      { method: 'GET' },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }
}

/**
 * Factory function to create PersonService instance.
 */
export const createPersonService = (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): PersonService => {
  return new PersonService(getAccessTokenSilently, getAccessTokenWithPopup);
};
